import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { shareCategorySet } from 'reducers/user';
import { tunnelSelectors } from 'reducers';
import { useDistributorShareCategories, useSmallScreen } from 'utils/hooks';
import { texts } from 'utils/languages/fr';

import { ActionButton, CustomDialogTitle, Loader, StyledRadio } from 'components';
import { RadioGroup, DialogContent, DialogActions, Dialog, FormControlLabel } from '@mui/material';
import * as icons from 'mdi-material-ui';

const optionSx = { alignItems: 'flex-start', fontSize: '16px', color: 'grey.600' };

const CategoryOption = ({ category }) => (
    <FormControlLabel
        value={category.id}
        sx={optionSx}
        control={<StyledRadio />}
        label={
            category.private
                ? texts.REMUNERATION_BY_HONORARY_INDIRECT + ` - ${texts.SHARE} ${category.name}`
                : texts.REMUNERATION_BY_RETROCESSION_INDIRECT + ` - ${texts.SHARE} ${category.name}`
        }
    />
);

CategoryOption.propTypes = { category: PropTypes.object };

// this component exists for the sole purpose of chosing a share category for indirect subscriptions
// it is used in multiple investor subscription flows that pass the `onConfirm` function to advance in the flow
const ShareCategoryDialog = ({ open, onClose, onConfirm }) => {
    const fullScreen = useSmallScreen();
    const dispatch = useDispatch();
    const currentInvestmentFund = useSelector(tunnelSelectors.getCurrentInvestmentFund);

    const [filteredCategories, { isLoading: categoriesLoader }] = useDistributorShareCategories(
        currentInvestmentFund?.id
    );

    const [shareCategoryId, setShareCategoryId] = useState('');

    const handleChange = ({ target: { value } }) => {
        setShareCategoryId(value);
    };

    const handleConfirm = useCallback(() => {
        const selectedCategory = filteredCategories.find(cat => cat.id === +shareCategoryId);
        dispatch(shareCategorySet(selectedCategory));
        onConfirm();
    }, [onConfirm, shareCategoryId, dispatch, filteredCategories]);

    useEffect(() => {
        if (filteredCategories.length === 1 && open) {
            dispatch(shareCategorySet(filteredCategories[0]));
            onConfirm();
        }
    }, [dispatch, filteredCategories, onConfirm, open]);

    return (
        <Dialog fullScreen={fullScreen} open={open} maxWidth="md">
            <CustomDialogTitle title={texts.CHOOSE_SHARE_CATEGORY} handleClose={onClose} />
            <DialogContent
                sx={theme => ({
                    padding: theme.spacing(2, 3),
                    color: theme.palette.grey[600],
                })}
            >
                <Loader lodinng={categoriesLoader}>
                    <RadioGroup
                        row
                        name="cleanShareChoice"
                        sx={{ display: 'flex', flexWrap: 'wrap', flexDirection: 'column' }}
                        value={shareCategoryId}
                        onChange={handleChange}
                    >
                        {filteredCategories.map(cat => (
                            <CategoryOption key={cat.id} category={cat} />
                        ))}
                    </RadioGroup>
                </Loader>
            </DialogContent>
            <DialogActions>
                <ActionButton
                    disabled={shareCategoryId === ''}
                    onClick={handleConfirm}
                    sx={theme => ({
                        letterSpacing: '1.3px',
                        '&:hover': { background: 'inherit' },
                        [theme.breakpoints.down('sm')]: { width: '100%' },
                    })}
                >
                    {texts.CONTINUER} <icons.ArrowRight sx={{ fontSize: '16px' }} />
                </ActionButton>
            </DialogActions>
        </Dialog>
    );
};

ShareCategoryDialog.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    onConfirm: PropTypes.func,
};

export default ShareCategoryDialog;
